import {StyleSheet} from "aphrodite";

import {DetailCell} from "@khanacademy/wonder-blocks-cell";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {
    PhosphorIcon,
    type PhosphorIconAsset,
} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {
    border,
    semanticColor,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingXSmall,
    LabelLarge,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {formatStandards, type MappedStandard} from "@khan/guide-core";

import xIcon from "@phosphor-icons/core/regular/x.svg";

interface Props {
    title: string;
    chosen?: boolean;
    disabled?: boolean;
    contentDescriptor: string;
    courseTitle: string;
    description: string;
    icon?: PhosphorIconAsset;
    mappedStandards?: MappedStandard[];
    onClick: (contentDescriptor: string) => void;
}

export default function ClickableContentCell(props: Props) {
    const {
        chosen,
        disabled,
        contentDescriptor,
        title,
        description,
        mappedStandards,
        courseTitle,
        icon,
        onClick,
    } = props;

    let subtitle = (
        <LabelSmall style={styles.description}>{description}</LabelSmall>
    );
    if (mappedStandards?.length) {
        subtitle = (
            <View>
                {subtitle}
                <Strut size={spacing.xxxxSmall_2} />
                <LabelSmall style={styles.standards}>
                    {formatStandards(mappedStandards)}
                </LabelSmall>
            </View>
        );
    }

    const renderCell = (hovered: boolean) => (
        <DetailCell
            styles={{
                root: [
                    hovered && !chosen && styles.cellHovered,
                    chosen && styles.cellChosen,
                    !contentDescriptor && styles.finalCell,
                ],
            }}
            title={
                <LabelLarge
                    style={[
                        styles.title,
                        hovered && !chosen && styles.titleHovered,
                    ]}
                >
                    {title}
                </LabelLarge>
            }
            subtitle1={
                contentDescriptor ? (
                    <View
                        style={[
                            styles.subtitle,
                            hovered && !chosen && styles.titleHovered,
                        ]}
                    >
                        <HeadingXSmall>{courseTitle}</HeadingXSmall>
                    </View>
                ) : undefined
            }
            subtitle2={subtitle}
            leftAccessory={
                icon ? (
                    <PhosphorIcon
                        icon={icon}
                        size="medium"
                        color={
                            hovered && !chosen
                                ? semanticColor.core.foreground.knockout.default
                                : semanticColor.core.foreground.neutral.subtle
                        }
                    />
                ) : null
            }
            rightAccessory={
                chosen && !disabled ? (
                    <IconButton
                        icon={xIcon}
                        size="medium"
                        kind="tertiary"
                        actionType="neutral"
                        onClick={() => onClick("")}
                    />
                ) : null
            }
        />
    );

    if (chosen) {
        return renderCell(false);
    }

    return (
        <Clickable onClick={() => onClick(contentDescriptor)}>
            {({hovered}) => renderCell(hovered)}
        </Clickable>
    );
}

const styles = StyleSheet.create({
    finalCell: {
        borderEndStartRadius: spacing.xxxSmall_4,
        borderEndEndRadius: spacing.xxxSmall_4,
    },
    cellChosen: {
        borderRadius: spacing.xxxSmall_4,
        border: `${border.width.thin} solid ${semanticColor.core.border.neutral.subtle}`,
    },
    cellHovered: {
        background: semanticColor.core.background.instructive.default,
        color: semanticColor.core.foreground.knockout.default,
    },
    title: {
        color: semanticColor.core.foreground.instructive.default,
    },
    titleHovered: {
        color: semanticColor.core.foreground.knockout.default,
    },
    subtitle: {
        flexDirection: "row",
        color: semanticColor.core.foreground.neutral.subtle,
    },
    description: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    standards: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
});
